import { t, useLanguage } from "../i18n";
import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrderStore } from '../store';
import { publicAPI } from '../services/api';
import { showBrowserNotification } from '../hooks/useWebSocket';
import { Clock, UtensilsCrossed, Package, Truck, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const STATUS_MAP = {
  awaiting_payment: {
    get label() {
      return t("Paiement à finaliser");
    },
    icon: Clock,
    color: 'bg-amber-600',
    dotColor: 'bg-amber-600',
    get notifText() {
      return t("Reprenez le paiement de votre commande");
    }
  },
  pending: {
    get label() {
      return t("En attente");
    },
    icon: Clock,
    color: 'bg-blue-600',
    dotColor: 'bg-blue-600',
    get notifText() {
      return t("Votre commande a ete recue");
    }
  },
  preparing: {
    get label() {
      return t("En preparation");
    },
    icon: UtensilsCrossed,
    color: 'bg-amber-500',
    dotColor: 'bg-amber-500',
    get notifText() {
      return t("Votre commande est en preparation !");
    }
  },
  ready: {
    get label() {
      return t("Prete !");
    },
    icon: Package,
    color: 'bg-green-500',
    dotColor: 'bg-green-500',
    get notifText() {
      return t("Votre commande est prete ! Rendez-vous au comptoir.");
    }
  },
  delivered: {
    get label() {
      return t("Livree");
    },
    icon: Truck,
    color: 'bg-slate-500',
    dotColor: 'bg-slate-500',
    get notifText() {
      return t("Votre commande a ete livree. Bon appetit !");
    }
  }
};
const STATUS_STEPS = ['pending', 'preparing', 'ready'];
export default function OrderTrackingBanner() {
  useLanguage();
  const navigate = useNavigate();
  const {
    activeOrderId,
    activeOrderStatus,
    activeTableNumber,
    updateOrderStatus,
    clearActiveOrder
  } = useOrderStore();
  const prevStatusRef = useRef(activeOrderStatus);
  const pollStatus = useCallback(async () => {
    if (!activeOrderId) return;
    try {
      const res = await publicAPI.getOrder(activeOrderId);
      const newStatus = res.data.status;
      if (prevStatusRef.current && newStatus !== prevStatusRef.current) {
        const config = STATUS_MAP[newStatus];
        if (config) {
          showBrowserNotification(`GlassOrder - ${config.label}`, config.notifText);
        }
      }
      prevStatusRef.current = newStatus;
      updateOrderStatus(newStatus);
      if (newStatus === 'delivered') {
        setTimeout(() => clearActiveOrder(), 30000);
      }
    } catch (err) {
      clearActiveOrder();
    }
  }, [activeOrderId, updateOrderStatus, clearActiveOrder]);
  useEffect(() => {
    if (!activeOrderId || activeOrderStatus === 'cancelled') return;
    pollStatus();
    const interval = setInterval(pollStatus, 5000);
    return () => clearInterval(interval);
  }, [activeOrderId, activeOrderStatus, pollStatus]);
  if (!activeOrderId || activeOrderStatus === 'cancelled') return null;
  const config = STATUS_MAP[activeOrderStatus] || STATUS_MAP.pending;
  const StatusIcon = config.icon;
  return <AnimatePresence>
      <motion.div initial={{
      y: -60,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: -60,
      opacity: 0
    }} className="relative z-30">

        <div onClick={() => navigate(`/order-confirmation?order_id=${activeOrderId}`)} className="mx-2 mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-lg transition-shadow hover:shadow-xl" data-testid="order-tracking-banner">

          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${config.color}`}>
            <StatusIcon className="h-5 w-5 text-white" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900" style={{
            fontFamily: 'Manrope'
          }}>
              {config.label}
            </p>

            <p className="text-xs text-slate-500">{t("Table")}{' '}
              {activeTableNumber}{' '}{t("- Appuyez pour voir les details")}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {activeOrderStatus !== 'delivered' && <div className="flex gap-1">
                {STATUS_STEPS.map(status => {
              const isActive = STATUS_STEPS.indexOf(activeOrderStatus) >= STATUS_STEPS.indexOf(status);
              const stepConfig = STATUS_MAP[status] || STATUS_MAP.pending;
              return <div key={status} className={`h-2 w-2 rounded-full ${isActive ? stepConfig.dotColor : 'bg-slate-200'}`} />;
            })}
              </div>}

            <button onClick={e => {
            e.stopPropagation();
            clearActiveOrder();
          }} aria-label={t('Fermer')} className="rounded-full p-1 transition-colors hover:bg-slate-100" data-testid="dismiss-tracking-banner">

              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}
